import classNames from "classnames";
import { Button, Checkbox, Radio } from "@edgetier/components";
import { faCheck, faSearch, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { Field, Formik, Form } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FunctionComponent, memo } from "react";
import { IFormValues, IProps } from "./advanced-search-phrase-filters.types";
import { formatEnumName, SearchHelp } from "@edgetier/client-components";
import { TextSearchType } from "@edgetier/watchtower-types";
import { VECTOR_SEARCH_ENABLED } from "constants/feature-flags";
import { VectorPhraseSearchOptions } from "../advanced-search-phrase.constants";
import { useKey } from "react-use";
import { isValueEmpty } from "utilities/is-value-empty";
import SimpleSearchHelp from "../simple-search-help";
import "./advanced-search-phrase-filters.scss";

/**
 * A component to display all the search options
 * @param props.applySearchFilters Submit all search options to explore filters form
 * @param props.closeMenu       Close search options menu
 * @param props.initialValues   Form initial values
 * @param props.name            Field name
 * @param props.placeholder     Field placeholder
 */
const AdvancedSearchPhraseFilters: FunctionComponent<IProps> = ({
    name,
    placeholder,
    closeMenu,
    applySearchFilters,
    initialValues,
}) => {
    // Close menu when escape is pressed
    useKey((event) => event.key === "Escape", closeMenu, { event: "keyup" });

    return (
        <Formik<IFormValues>
            initialValues={initialValues}
            onSubmit={(values) => {
                applySearchFilters(values);
                closeMenu();
            }}
        >
            {({ values, setFieldValue }) => (
                <Form autoComplete="off" name="advanced-search-phrase-form">
                    <div className="advanced-search-phrase__input" data-testid="advanced-search-phrase-input">
                        <div className="field field-inline">
                            <div className="field-inline__icon">
                                <FontAwesomeIcon icon={faSearch} />
                            </div>
                            <Field
                                autoFocus
                                autoComplete="off"
                                placeholder={placeholder}
                                type="text"
                                name={name}
                                aria-label={name}
                                className="search-phrase"
                                id={`id-${name}`}
                            />

                            {!isValueEmpty(values.search) && (
                                <button
                                    onClick={() => setFieldValue(name, "")}
                                    type="button"
                                    className="advanced-search-phrase__input__remove-text"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                        {values.textSearchType === TextSearchType.Vector && <SearchHelp />}
                        {values.textSearchType === TextSearchType.Simple && <SimpleSearchHelp />}
                    </div>
                    <div
                        className={classNames("advanced-search-phrase__options", {
                            "advanced-search-phrase__options--vector-search-enabled": VECTOR_SEARCH_ENABLED,
                            "advanced-search-phrase__options--vector-search-disabled": !VECTOR_SEARCH_ENABLED,
                        })}
                    >
                        {VECTOR_SEARCH_ENABLED && (
                            <div>
                                <label className="advanced-search-phrase__advanced-options-label">
                                    <span>Search Types</span>
                                </label>
                                <div className="advanced-search-phrase__search-types">
                                    {Object.entries(TextSearchType).map(([label, option]) => (
                                        <div className="field" key={option}>
                                            <Radio label={label} name="textSearchType" option={option} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <div className="advanced-search-phrase__advanced-options">
                                {Object.entries(VectorPhraseSearchOptions).map(([label, name]) => (
                                    <div className="field" key={name}>
                                        <Checkbox
                                            label={`${formatEnumName(label)}${
                                                name === VectorPhraseSearchOptions.InteractionDetails ? "" : " Messages"
                                            } `}
                                            name={name}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="advanced-search-phrase__controls">
                        <Button icon={faTimes} type="button" onClick={closeMenu} styleName="neutral">
                            Cancel
                        </Button>

                        <Button icon={faCheck} type="submit" styleName="positive">
                            Apply
                        </Button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default memo(AdvancedSearchPhraseFilters);
